<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import Loading from '@/components/common/loading.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import { GRANT_TYPE_DISPLAY_NAME } from '@/utils/credit/constants';
import { formatDate } from '@/utils/formatters';
import { STATES, useManagementContractCreditGrants } from '@/utils/swr';

type AdminTableItem = {
  id: number;
  grantType: string;
  grantedCredit: number;
  createdAt: string;
  formattedCreatedAt: string;
};

const props = defineProps<{
  selectedCreditPeriodId?: number;
}>();

const route = useRoute();
const { createSnackbar } = useSnackbar();
const contractId = computed(() => Number(route.params.contractId));

const creditCreditPeriodId = computed(() => props.selectedCreditPeriodId ?? 0);
const { data: creditGrantsData, state: creditGrantsState } =
  useManagementContractCreditGrants(contractId, creditCreditPeriodId);
const isLoadingCreditGrants = computed(
  () =>
    creditGrantsState.value === STATES.PENDING ||
    creditGrantsState.value === STATES.VALIDATING,
);
const creditGrants = computed(() => creditGrantsData.value ?? []);

const items = computed<AdminTableItem[]>(() => {
  return creditGrants.value.map(grant => ({
    id: grant.id,
    grantType: GRANT_TYPE_DISPLAY_NAME[grant.grant_type],
    grantedCredit: grant.granted_credit,
    createdAt: grant.created_at,
    formattedCreatedAt: formatDate(grant.created_at),
  }));
});

watch(creditGrantsState, () => {
  if (creditGrantsState.value === STATES.ERROR) {
    createSnackbar({
      message: '付与履歴の取得に失敗しました。',
      type: 'error',
    });
  }
});

const fields: Field<AdminTableItem>[] = [
  {
    name: 'grantedCredit',
    displayName: '付与量',
    isSortable: true,
    type: 'number',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'grantType',
    displayName: '付与理由',
    isSortable: true,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'createdAt',
    displayName: '付与日',
    isSortable: true,
    type: 'date',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
];
const initialSortField = fields.find(f => f.name === 'createdAt');
</script>

<template>
  <Loading class="loading" v-if="isLoadingCreditGrants" />
  <div v-else>
    <div class="title">
      <span class="c-title-bold--m">付与履歴</span
      ><span class="count c-text-regular--s">・{{ items.length }}件</span>
    </div>
    <div class="spacing-16"></div>
    <AdminBaseTable
      :items="items"
      :fields="fields"
      :initial-sort-field="initialSortField"
      initial-sort-order="desc"
    >
      <template #empty-tbody>付与履歴はありません。</template>
      <template #cell-grantedCredit="{ item }">
        {{ item.grantedCredit.toLocaleString() }}
      </template>
      <template #cell-grantType="{ item }">
        {{ item.grantType }}
      </template>
      <template #cell-createdAt="{ item }">
        {{ item.formattedCreatedAt }}
      </template>
    </AdminBaseTable>
  </div>
</template>

<style lang="scss" scoped>
.loading {
  margin-top: -24px; // 先祖のsrc/components/layouts/admin-content.vueのmargin-topを相殺
  height: calc(
    100vh - 64px - 91px - 41px
  ); // 画面全体の高さ - グローバルヘッダーの高さ - ページヘッダーの高さ - タブの高さ
}
.title {
  display: flex;
  align-items: center;
  .count {
    color: $color-gray600;
  }
}
</style>
